import React from "react";
import axios from "axios";
import { trackQuoteSubmit, trackPhoneClick } from "../../utils/conversions";
import { getAttribution } from "../../utils/attribution";
import "../css/ContactGrid.css";

const SERVICE_OPTIONS = [
  "Industrial Moving & Relocation",
  "Machinery Rigging & Installation",
  "Warehousing & Storage",
  "Other / Not sure yet",
];

const HEAR_OPTIONS = ["Google", "Referral", "Instagram", "Facebook", "Sign", "Other"];

const INITIAL = {
  name: "",
  companyName: "",
  email: "",
  phoneNumber: "",
  serviceType: "",
  howDidYouHear: "",
  details: "",
};

function ContactGrid() {
  const [formData, setFormData] = React.useState(INITIAL);
  const [status, setStatus] = React.useState({ state: "idle", message: "" });

  const update = (field) => (e) =>
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "sending", message: "Sending your request…" });

    // Keys here are the contract the backend (/api/sendmail) reads.
    const payload = {
      name: formData.name.trim(),
      companyName: formData.companyName.trim(),
      email: formData.email.trim(),
      phoneNumber: formData.phoneNumber.trim(),
      serviceType: formData.serviceType,
      howDidYouHear: formData.howDidYouHear,
      details: formData.details.trim(),
      // How the visitor reached the site (e.g. an ad click days ago), so the
      // lead can be traced to the ad in the admin's Leads & Sales report.
      page: window.location.pathname,
      attribution: getAttribution(),
    };

    try {
      const { status: httpStatus } = await axios.post("/api/sendmail", payload);
      if (httpStatus === 200) {
        trackQuoteSubmit();
        setFormData(INITIAL);
        setStatus({
          state: "success",
          message: "Thanks — your request is in. Our team will reach out shortly.",
        });
      } else {
        setStatus({
          state: "error",
          message: "Something went wrong. Please try again in a moment.",
        });
      }
    } catch (err) {
      console.error("Error sending quote request:", err);
      setStatus({
        state: "error",
        message: "We couldn't send that. Please retry or call us directly.",
      });
    }
  };

  return (
    <section className="QuotePage">
      <div className="QuoteShell">
        <aside className="QuoteAside">
          <span className="QuoteEyebrow">Request a quote</span>
          <h1 className="QuoteHeading">
            Let's scope your <span>industrial project</span>.
          </h1>
          <p className="QuoteSub">
            Tell us what you need moved, rigged, installed, or stored. We'll get
            back to you with a tailored quote — usually within one business day.
          </p>

          <div className="QuoteContactCard">
            <span className="QuoteContactLabel">Prefer to talk now?</span>
            <a
              href="tel:+17782389933"
              onClick={trackPhoneClick}
              className="QuotePhone"
            >
              <span className="QuotePhoneIcon" aria-hidden="true">
                📞
              </span>
              778-238-9933
            </a>
            <span className="QuoteHours">Mon–Fri · 7:00 AM – 6:00 PM</span>
          </div>

          <ul className="QuoteTrust">
            <li>Licensed &amp; insured industrial crews</li>
            <li>Heavy machinery rigging specialists</li>
            <li>Serving Vancouver &amp; the Lower Mainland</li>
          </ul>
        </aside>

        <div className="QuoteFormPanel">
          <form className="QuoteForm" onSubmit={handleSubmit} noValidate>
            <div className="QuoteRow">
              <div className="QuoteField">
                <label htmlFor="qf-name">Full name *</label>
                <input
                  id="qf-name"
                  type="text"
                  autoComplete="name"
                  required
                  value={formData.name}
                  onChange={update("name")}
                  placeholder="Jane Smith"
                />
              </div>
              <div className="QuoteField">
                <label htmlFor="qf-company">Company name *</label>
                <input
                  id="qf-company"
                  type="text"
                  autoComplete="organization"
                  required
                  value={formData.companyName}
                  onChange={update("companyName")}
                  placeholder="Acme Manufacturing"
                />
              </div>
            </div>

            <div className="QuoteRow">
              <div className="QuoteField">
                <label htmlFor="qf-email">Email address *</label>
                <input
                  id="qf-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={update("email")}
                  placeholder="you@company.com"
                />
              </div>
              <div className="QuoteField">
                <label htmlFor="qf-phone">Phone number *</label>
                <input
                  id="qf-phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={formData.phoneNumber}
                  onChange={update("phoneNumber")}
                  placeholder="(778) 555-0123"
                />
              </div>
            </div>

            <div className="QuoteField">
              <label htmlFor="qf-service">What do you need? *</label>
              <select
                id="qf-service"
                required
                value={formData.serviceType}
                onChange={update("serviceType")}
              >
                <option value="" disabled>
                  Select a service…
                </option>
                {SERVICE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="QuoteField">
              <label htmlFor="qf-hear">How did you hear about us?</label>
              <select
                id="qf-hear"
                value={formData.howDidYouHear}
                onChange={update("howDidYouHear")}
              >
                <option value="">Select an option…</option>
                {HEAR_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div className="QuoteField">
              <label htmlFor="qf-details">Project details *</label>
              <textarea
                id="qf-details"
                rows="5"
                required
                value={formData.details}
                onChange={update("details")}
                placeholder="What needs to be moved or handled, equipment weights/dimensions, site access, timeline, and location."
              />
            </div>

            <button
              type="submit"
              className="QuoteSubmit"
              disabled={status.state === "sending"}
            >
              {status.state === "sending" ? "Sending…" : "Send request"}
            </button>

            {status.message && (
              <p className={`QuoteStatus QuoteStatus--${status.state}`} role="status">
                {status.message}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

export default ContactGrid;
